/**
 * DealerAuditPopup Component
 * Popup for dealer check-in audit details
 * Feature-based architecture implementation
 */

import React, { useState } from "react";
import ModalPopup from "@components/Common/ModalPopup";
import Loader from "@features/shared/components/Loader";
import styles from "./DealerAuditPopup.module.css";
import { uploadFileToBlobStorage } from "../../../services/BlobStorage/uploadBlobApi";

const DealerAuditPopup = ({ selectLatLong, isOpen, setIsOpen, onSubmit }) => {
  const [selectedOption, setSelectedOption] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setUploadError("");

      let fileUrl = null;

      // Upload file to blob storage if "Non-Darpan bills available" is selected and file exists
      if (selectedOption === "Non-Darpan bills available" && file) {
        try {
          const currentDate = new Date();
          const year = currentDate.getFullYear();
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const month = monthNames[currentDate.getMonth()];
          const day = String(currentDate.getDate()).padStart(2, '0');
          const dateStr = `${year}${String(currentDate.getMonth() + 1).padStart(2, '0')}${day}`;
          const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
          const folder = `dealer-audit-bills/${year}/${month}/${selectLatLong?.Customer_Code__c} - ${selectLatLong?.CustomerName__c}`;
          const fileName = `${selectLatLong?.DealerCode__c} - ${selectLatLong?.Dealer_Name__c} - ${dateStr}${fileExtension}`;

          // Create a new File object with the custom filename
          const renamedFile = new File([file], fileName, { type: file.type });
          const uploadResponse = await uploadFileToBlobStorage(renamedFile, folder);
          if (uploadResponse.success) {
            fileUrl = uploadResponse.data.url;
          } else {
            throw new Error("File upload failed");
          }
        } catch (err) {
          console.error("Error uploading file:", err);
          setUploadError("Failed to upload file. Please try again.");
          setLoading(false);
          return;
        }
      }

      const payload = {
        Dealer_Check_in: selectedOption,
        non_darphan_bill_file_url: fileUrl,
      };

      onSubmit(payload);
      setLoading(false);
      setIsOpen(false);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setUploadError("An error occurred. Please try again.");
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <ModalPopup
      isModalOpen={isOpen}
      setIsModalOpen={setIsOpen}
      ModalMainClass="dealerAuditPopup"
      backdrop={false}
      fade={false}
      data={
        <div className={styles.popupContainer}>
          <Loader loading={loading} />
          <h4 className={styles.popupTitle}>Dealer Check-in Details</h4>

          {uploadError && (
            <div style={{ color: "red", marginBottom: "10px", fontSize: "14px" }}>
              {uploadError}
            </div>
          )}

          {[
            { label: "Darpan bills available", key: "Darpan bills available" },
            {
              label: "Non-Darpan bills available",
              key: "Non-Darpan bills available",
            },
            {
              label: "Bill with CA / Accountant",
              key: "Bill with CA / Accountant",
            },
            { label: "Owner not available", key: "Owner not available" },
            {
              label: "Dual Bill / Printed on back",
              key: "Dual Bill / Printed on back",
            },
            {
              label: "NO purchase",
              key: "NO purchase"
            }
          ].map((item) => (
            <label key={item.key} className={styles.radioLabel}>
              <input
                type="radio"
                name="dealerAuditOption"
                value={item.key}
                checked={selectedOption === item.key}
                onChange={() => setSelectedOption(item.key)}
              />
              {item.label}
            </label>
          ))}

          {selectedOption === "Non-Darpan bills available" && (
            <div className={styles.fileUploadBox}>
              <label className={styles.fileLabel}>Upload Bill (optional)</label>
              <input
                type="file"
                accept="image/*"
                className={styles.fileInput}
                onChange={(e) => {
                  const selectedFile = e.target.files[0];
                  if (selectedFile) {
                    if (selectedFile.size > 5 * 1024 * 1024) {
                      alert("File size should not exceed 5MB");
                      return;
                    }
                    setFile(selectedFile);
                  }
                }}
              />
              {file && (
                <p style={{ marginTop: "8px", fontSize: "14px" }}>
                  Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
          )}
          <div className={styles.buttonGroup}>
            <button
              className={`${styles.btn} ${styles.btnCancel}`}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              className={`${styles.btn} ${styles.btnSubmit}`}
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>
        </div>
      }
    />
  );
};

export default DealerAuditPopup;
